import { useMemo } from "react";
import { InlineSelect } from "~/shared/components/InlineSelect";
import {
  findMatchingPreset,
  formatMinutesAsTime,
  getTimeSlotOptions,
  WINDOW_TIME_STEP_MINUTES,
  getWindowMinutes,
  hhmmToMinutes,
  minutesToHHmm,
  type DeliveryWindowPreset,
  type WindowLike,
  type WindowMinutes,
} from "~/shared/utils/delivery-window";
import { WindowPresetSelect } from "~/features/orders/components/WindowPresetSelect";

/**
 * The window a dispatcher has chosen in a side panel: either one of the presets,
 * or a custom From/To range. Only one is active at a time; the last one touched wins.
 */
export type WindowChoice =
  | { mode: "preset"; preset: DeliveryWindowPreset }
  | { mode: "custom"; from: string; to: string };

/** Starts from the order's current window: a matching preset, otherwise a custom range, otherwise the first preset. */
export function getInitialWindowChoice(
  window: WindowLike | null | undefined,
  presets: readonly DeliveryWindowPreset[]
): WindowChoice | null {
  const preset = findMatchingPreset(presets, window);
  if (preset) return { mode: "preset", preset };
  const minutes = getWindowMinutes(window);
  if (minutes) return { mode: "custom", from: minutesToHHmm(minutes.startMinutes), to: minutesToHHmm(minutes.endMinutes) };
  return presets[0] ? { mode: "preset", preset: presets[0] } : null;
}

/** The chosen window in minutes, or null while the choice is incomplete or invalid. */
export function getWindowChoiceMinutes(choice: WindowChoice | null): WindowMinutes | null {
  if (!choice) return null;
  if (choice.mode === "preset") return getWindowMinutes(choice.preset);
  const startMinutes = hhmmToMinutes(choice.from);
  const endMinutes = hhmmToMinutes(choice.to);
  if (startMinutes === null || endMinutes === null || endMinutes <= startMinutes) return null;
  return { startMinutes, endMinutes };
}

/**
 * The chosen window as a full window object { start, end, label, startMinutes, endMinutes }, mirroring the backend:
 * standard windows keep their named label, everything else gets an empty label; hours cover the exact range.
 * Used by screens whose APIs take a window object (Transfer Queue, Scheduled).
 */
export function getWindowChoiceWindow(
  choice: WindowChoice | null,
  presets: readonly DeliveryWindowPreset[]
): { start: number; end: number; label: string; startMinutes: number; endMinutes: number } | null {
  const minutes = getWindowChoiceMinutes(choice);
  if (!minutes) return null;
  const standard = presets.find(
    (p) => p.group === "standard" && p.startMinutes === minutes.startMinutes && p.endMinutes === minutes.endMinutes
  );
  return {
    start: Math.floor(minutes.startMinutes / 60),
    end: Math.ceil(minutes.endMinutes / 60),
    label: standard?.label ?? "",
    ...minutes,
  };
}

/** True while a custom range is only half filled in (save should wait for it). */
export function isWindowChoiceIncomplete(choice: WindowChoice | null): boolean {
  return choice?.mode === "custom" && getWindowChoiceMinutes(choice) === null;
}

type WindowChoiceFieldsProps = Readonly<{
  value: WindowChoice | null;
  onChange: (next: WindowChoice) => void;
  presets: readonly DeliveryWindowPreset[];
  disabled?: boolean;
  /** Heading of the preset field (e.g. "Confirmed Window" on Scheduled) */
  windowLabel?: string;
  /** Heading of the custom From/To field */
  customLabel?: string;
  /** Gap between a heading and its control; matches the hosting panel's spacing */
  labelGap?: number;
}>;

/** Adds the current value to the options when it is not on the 15-minute list (e.g. an existing off-grid window). */
function withCurrentOption(
  options: Array<{ value: string; label: string; minutes: number }>,
  current: string
): Array<{ value: string; label: string; minutes: number }> {
  const minutes = hhmmToMinutes(current);
  if (minutes === null || options.some((o) => o.value === current)) return options;
  return [...options, { value: current, label: formatMinutesAsTime(minutes).toLowerCase(), minutes }].sort(
    (a, b) => a.minutes - b.minutes
  );
}

export function WindowChoiceFields({
  value,
  onChange,
  presets,
  disabled,
  windowLabel = "Window",
  customLabel = "Choose custom window",
  labelGap = 6,
}: WindowChoiceFieldsProps) {
  const slots = useMemo(() => getTimeSlotOptions(WINDOW_TIME_STEP_MINUTES), []);
  const customFrom = value?.mode === "custom" ? value.from : "";
  const customTo = value?.mode === "custom" ? value.to : "";
  const fromMinutes = hhmmToMinutes(customFrom);

  // "From" cannot be the last slot (nothing would be left for "To"); "To" must be after "From".
  const fromOptions = useMemo(
    () => withCurrentOption(slots.slice(0, -1), customFrom),
    [slots, customFrom]
  );
  const toOptions = useMemo(
    () => withCurrentOption(fromMinutes === null ? slots.slice(1) : slots.filter((s) => s.minutes > fromMinutes), customTo),
    [slots, fromMinutes, customTo]
  );

  function onFromChange(from: string) {
    const nextFrom = hhmmToMinutes(from);
    const currentTo = hhmmToMinutes(customTo);
    // Keep "To" only if it is still after the new "From"
    const to = nextFrom !== null && currentTo !== null && currentTo > nextFrom ? customTo : "";
    onChange({ mode: "custom", from, to });
  }

  return (
    <>
      <div style={{ display: "grid", gap: labelGap }}>
        <span style={{ fontSize: 14, fontWeight: 600 }}>{windowLabel}</span>
        <WindowPresetSelect
          variant="field"
          value={value?.mode === "preset" ? value.preset : null}
          presets={presets}
          onSelect={(preset) => onChange({ mode: "preset", preset })}
          disabled={(disabled ?? false) || presets.length === 0}
          placeholder={value?.mode === "custom" ? "Using custom window" : "Select a window"}
        />
      </div>

      <div style={{ display: "grid", gap: labelGap }}>
        <span style={{ fontSize: 14, fontWeight: 600 }}>{customLabel}</span>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <InlineSelect
              value={customFrom}
              options={fromOptions}
              onChange={onFromChange}
              disabled={disabled}
              placeholder="From"
              ariaLabel="Custom window start"
            />
          </div>
          <span style={{ color: "var(--text-muted)" }}>-</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <InlineSelect
              value={customTo}
              options={toOptions}
              onChange={(to) => onChange({ mode: "custom", from: customFrom, to })}
              disabled={disabled}
              placeholder="To"
              ariaLabel="Custom window end"
            />
          </div>
        </div>
        {value?.mode === "custom" && (!customFrom || !customTo) && (
          <span style={{ fontSize: 12, color: "var(--text-muted)" }}>Select both a start and an end time.</span>
        )}
      </div>
    </>
  );
}
